import { memo, Suspense, useCallback, useMemo } from 'react'
import { ChannelList } from 'stream-chat-react'

import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useDebouncedValue } from 'src/shared/hooks/use-debounced-value'
import type { ChatType } from 'src/shared/schemas/chats/chat-data'

import { useChatSearch } from '../../api/use-chat-channels'
import { useChatClient } from '../../context/chat-context'
import ChatListSkeleton from '../../skeletons/chat-list-skeleton'
import useChatStore from '../../store/chat-store'
import ChatPreview from '../chat-preview'

import { StreamChannelList } from './stream-list-wrapper'

const ALL_CHAT_TYPES = ['partner_match', 'referral', 'network', 'sneak'] as ChatType[]

const MemoizedStreamChannelList = memo(StreamChannelList)

const ConversationListContent = () => {
  const { client } = useChatClient()
  const { data: currentUser } = useMyUserData()
  const query = useChatStore((state) => state.query)
  const debouncedQuery = useDebouncedValue<string>(query, 300)

  const userId = client?.user?.id || currentUser?.id

  const {
    data: allUserIds,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatSearch(debouncedQuery, ALL_CHAT_TYPES, 20)

  const allUserIdList = useMemo(() => allUserIds?.pages.flatMap((page) => page?.channelIds ?? []) ?? [], [allUserIds])
  const filteredIds = useMemo(() => allUserIdList, [allUserIdList])
  const idFilter = useMemo(() => (filteredIds.length > 0 ? { $in: filteredIds } : undefined), [filteredIds])

  const filters = useMemo(() => {
    if (!userId) return {}

    return {
      members: { $in: [userId.toString()] },
      type: {
        $in: ['messaging', 'network'],
      },
      cid: idFilter,
    }
  }, [userId, idFilter])

  const handleEndReached = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <ChannelList
      List={(listProps) => (
        <MemoizedStreamChannelList {...listProps} isLoadingMore={isFetchingNextPage} onEndReached={handleEndReached} />
      )}
      filters={filters}
      Preview={ChatPreview}
    />
  )
}

const ConversationList = () => {
  return (
    <Suspense fallback={<ChatListSkeleton />}>
      <ConversationListContent />
    </Suspense>
  )
}

export default ConversationList
